import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Calendar, List, Search, Plus, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { JournalCalendar } from "@/components/JournalCalendar";
import { JournalList } from "@/components/JournalList";
import { CreateJournalModal } from "@/components/CreateJournalModal";
import type { DailyJournal } from "@/types";

type ViewMode = "calendar" | "list";

/**
 * Journals page for viewing and managing daily learning journals
 * Supports calendar and list views with search functionality
 *
 * @returns Journal management interface
 */
export function Journals() {
  const { getToken } = useAuth();
  const [journals, setJournals] = useState<DailyJournal[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const token = await getToken();
        if (!token) return;

        const data = await api.getAllJournals(token);
        setJournals(data);
      } catch (error) {
        console.error("Failed to fetch journals:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJournals();
  }, [getToken]);

  // Filter journals by search query
  const filteredJournals = journals.filter((journal) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      journal.overallSummary.toLowerCase().includes(query) ||
      journal.nextSteps?.toLowerCase().includes(query) ||
      journal.moodReflection?.toLowerCase().includes(query)
    );
  });

  /**
   * Handle journal creation
   */
  const handleCreateJournal = async (date: string) => {
    try {
      const token = await getToken();
      if (!token) return;

      const newJournal = await api.generateDailyJournal(token, date);
      setJournals((prev) => [newJournal, ...prev]);
      setShowCreateModal(false);
    } catch (error) {
      console.error("Failed to create journal:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse-subtle text-primary-600">
          <Sparkles size={48} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-primary-950">
            Learning Journals
          </h1>
          <p className="text-slate-500 mt-1">
            Review your daily learning progress
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-medium shadow-lg shadow-primary-600/25 hover:bg-primary-700 transition-colors"
        >
          <Plus size={20} />
          Create Journal
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={20}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search journals..."
            className="w-full pl-10 pr-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        {/* View mode toggle */}
        <div className="flex bg-white border rounded-xl p-1">
          <button
            onClick={() => setViewMode("calendar")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === "calendar"
                ? "bg-primary-600 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Calendar size={18} />
            <span className="hidden sm:inline">Calendar</span>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === "list"
                ? "bg-primary-600 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <List size={18} />
            <span className="hidden sm:inline">List</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {filteredJournals.length === 0 ? (
        <div className="bg-white rounded-2xl border p-12 text-center">
          <Calendar className="mx-auto text-slate-300 mb-4" size={64} />
          <h3 className="text-xl font-display font-semibold text-slate-700">
            No journals found
          </h3>
          <p className="text-slate-500 mt-2">
            {searchQuery
              ? "Try a different search term"
              : "Start chatting to create your first journal"}
          </p>
        </div>
      ) : viewMode === "calendar" ? (
        <JournalCalendar journals={filteredJournals} />
      ) : (
        <JournalList journals={filteredJournals} />
      )}

      {/* Create modal */}
      {showCreateModal && (
        <CreateJournalModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateJournal}
        />
      )}
    </div>
  );
}

